/* eslint-disable no-useless-escape */
import React, { useState, useEffect, ChangeEvent, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { TextInput, DatePicker, PasswordErrorList, Button, OtpInput, Checkbox, ConfirmationDialog } from "../../components";
import { formatPhoneNumber, validatePassword } from "../../utils";
import NumberFormat from "react-number-format";
import { Phone_Fax_Regex, PrimaryContactEmail, colors } from "../../utils/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  RequestFormInitDataType,
  sendEmailVerificationCode,
  sendPhoneVerificationCode,
  updateRequestFormData,
  verifyEmailCode,
  verifyPhoneCode,
} from "../../redux/slices/OnBoardingSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { acceptPatientInvitation } from "../../redux/slices/OfficeSlice";
import { AuthContext } from "../../contexts/AuthContext/AuthContext";
import { emailExists } from "../../redux/slices/CurrentUserSlice";
import { User } from "../../models/user.model";
import TermsAndConditions from "../Guest/TermsAndCondition";
import PrivacyPolicy from "../Guest/PrivacyPolicy";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { calculateAge, isValidDate } from "../../utils/date";
import HomePageHeader from "../Onboarding/HomePageHeader";

enum PatientInviteSteps {
  Register = "register",
  VerifyEmail = "verifyEmail",
  VerfifyPhone = "verifyPhone",
}

export const requestFormInitData: RequestFormInitDataType[] = [
  {
    id: "firstName",
    label: "First Name",
    placeholder: "Enter first name",
    required: true,
    errorMesage: "Please provide a first name",
    value: "",
    isValid: false,
  },
  {
    id: "lastName",
    label: "Last Name",
    placeholder: "Enter last name",
    required: true,
    errorMesage: "Please provide a last name",
    value: "",
    isValid: false,
  },
  {
    id: "email",
    label: "Email",
    placeholder: "Enter email address",
    required: true,
    errorMesage: "Please enter a valid email address",
    value: "",
    isValid: false,
  },
  {
    id: "birthday",
    label: "Date of Birth",
    placeholder: "MM/DD/YYYY",
    required: true,
    errorMesage: "Valid date required",
    value: "",
    isValid: true,
  },
  {
    id: "phone",
    label: "Mobile Phone Number",
    placeholder: "Enter mobile number",
    required: true,
    errorMesage: "Please provide a mobile number",
    value: "",
    isValid: false,
  },
  {
    id: "ohip",
    label: "Health Card Number",
    placeholder: "Health Card #",
    required: true,
    errorMesage: "Valid Health Card Number required",
    value: "",
    isValid: true,
  },
  {
    id: "password",
    label: "Password",
    placeholder: "Enter Password",
    required: true,
    errorMesage: "Please provide a password",
    value: "",
    isValid: false,
  },
  {
    id: "confirmPassword",
    label: "Confirm Password",
    placeholder: "Confirm password",
    required: true,
    errorMesage: "Please provide a confirm password",
    value: "",
    isValid: false,
  },
];

const validationsInitData = [
  {
    id: "lowerCase",
    isValid: false,
    msg: "Password must contain a lower case letter",
  },
  {
    id: "upperCase",
    isValid: false,
    msg: "Password must contain an upper case letter",
  },
  {
    id: "specialCharacter",
    isValid: false,
    msg: "Password must contain a special character",
  },
  {
    id: "cotainNumber",
    isValid: false,
    msg: "Password must contain a number",
  },
  {
    id: "eightCharacters",
    isValid: false,
    msg: "Password must contain at least  8 characters",
  },
  { id: "passwordMatch", isValid: false, msg: "Password must match" },
];

const VerifyEmail = ({ email, emailVerified, onBack }: { email: string; emailVerified: () => void; onBack: () => void }) => {
  const [error, setError] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [sendingOTP, setSendingOTP] = useState(false);
  const [otpInput, setOtpInput] = useState("");

  const onContinueClick = () => {
    setError(false);
    setSendingOTP(true);
    dispatch(verifyEmailCode({ email: email, code: otpInput })).then(action => {
      if (action.payload) {
        emailVerified();
      } else {
        setSendingOTP(false);
        setError(true);
      }
    });
  };
  useEffect(() => {
    if (otpInput.length === 6) {
      onContinueClick();
    } else {
      setError(false);
    }
  }, [otpInput]);
  return (
    <div className="flex flex-col items-start">
      <h3 className="text-3xl font-light mb-2">Email Verification</h3>
      <p className="text-base mb-5">You have received a 6-digit code to the email {email}. Enter it below to verify your account.</p>
      <OtpInput length={6} onChange={e => setOtpInput(e)} />
      {error ? <p style={{ color: colors.Red }}>Invalid code, please try again</p> : null}
      <div className="cursor-pointer text-docsigna-purple-dark font-medium mt-2 w-fit" onClick={onBack}>
        <KeyboardBackspaceIcon /> Back
      </div>
      <div className="tab-content py-5 w-80">
        <Button
          text="Continue"
          width="full"
          disabled={otpInput.length !== 6}
          onClick={() => onContinueClick()}
          variant={sendingOTP ? "loading" : "Primary"}
        />
      </div>
    </div>
  );
};

const VerifyPhone = ({ phone, phoneVerified, onBack }: { phone: string; phoneVerified: () => void; onBack: () => void }) => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const [otpInput, setOtpInput] = useState("");
  const [submitError, setSubmitError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpError, setOtpError] = useState(false);

  const handleTryAgain = () => {
    setOtpError(false);
    setOtpInput("");
    dispatch(sendPhoneVerificationCode({ phone })).then(action => {
      if (action?.payload !== null) {
        toast?.openToast("Code has been sent again.");
      }
    });
  };

  const onSubmitClick = () => {
    setOtpError(false);
    setLoading(true);
    dispatch(verifyPhoneCode({ phone: phone, code: otpInput })).then(action => {
      if (action.payload) {
        phoneVerified();
      } else {
        setLoading(false);
        setOtpError(true);
      }
    });
  };

  useEffect(() => {
    if (loading) {
      setSubmitError(false);
    }
  }, [loading]);

  useEffect(() => {
    if (otpInput.length === 6) {
      onSubmitClick();
    } else {
      setSubmitError(false);
    }
  }, [otpInput]);
  return (
    <div className="flex flex-col items-start">
      <h3 className="text-3xl font-light mb-2">We need to validate your phone number.</h3>
      <p className="text-base mb-5 font-medium">We have sent a 6 digit code to your phone number: +1-{phone}.</p>
      <OtpInput length={6} onChange={e => setOtpInput(e)} />
      <div className="cursor-pointer text-docsigna-purple-dark font-medium mt-2 w-fit" onClick={onBack}>
        <KeyboardBackspaceIcon /> Back
      </div>
      <div className="tab-content py-5 w-80">
        <Button text="Continue" width="full" disabled={otpInput.length !== 6} onClick={onSubmitClick} variant={loading ? "loading" : "Primary"} />
      </div>
      {submitError ? <p style={{ color: colors.Red }}>{"Failed to submit the form. Please check the data you've entered."}</p> : null}
      {otpError ? <p style={{ color: colors.Red }}>{"Invalid code, please try again"}</p> : null}
      <br />
      <p className="text-base mb-5 font-medium">
        Didn’t receive a code?{" "}
        <span className="text-base text-docsigna-purple-dark cursor-pointer" onClick={() => handleTryAgain()}>
          Resend Code
        </span>{" "}
      </p>
    </div>
  );
};

const InvitationForm = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { invitationTokenDetails } = useSelector((state: RootState) => state.user);
  const officeId = invitationTokenDetails?.offices?.[0].officeId ?? "";
  const token = invitationTokenDetails?.token ?? "";
  const { pending } = useSelector((state: RootState) => state.onBoarding);
  const { loading } = useSelector((state: RootState) => state.currentUser);
  const [currentStep, setCurrentStep] = useState<PatientInviteSteps>(PatientInviteSteps.Register);
  const [formData, setFormData] = useState(requestFormInitData);
  const [pwdValidations, setPwdValidations] = useState(validationsInitData);
  const [errorInitiated, setErrorInitiated] = useState(false);
  const passwordFlds = ["password", "confirmPassword"];
  const [dateError, setDateError] = useState(true);
  const [ohipState, setOhipState] = useState(formData[5].value);
  const [sendingEmail] = useState(false);
  const [sendingEmailError, setSendingEmailError] = useState("");
  const [openTerms, setOpenTerms] = useState(false);
  const [openPrivacyPolicy, setOpenPrivacyPolicy] = useState(false);
  const [tcAccepted, setTcAccepted] = useState(false);
  const [showMethod, setShowMethod] = useState(false);
  const [dobErrorMsg, setDobErrorMsg] = useState("Valid date required");

  const handleTermsCheckbox = () => {
    setTcAccepted(!tcAccepted);
  };

  const handleClickOpenTerms = () => {
    setOpenTerms(true);
  };

  const handleClickOpenPrivacyPolicy = () => {
    setOpenPrivacyPolicy(true);
  };

  const handleCloseTerms = () => {
    setOpenTerms(false);
  };
  const handleClosePrivacyPolicy = () => {
    setOpenPrivacyPolicy(false);
  };

  const validateField = (name: string, value: string) => {
    let valid = false;
    switch (name) {
      case "firstName":
      case "lastName":
        valid = !!value.trim();
        break;
      case "phone":
        valid = Phone_Fax_Regex.test(value);
        break;
      case "email":
        valid = PrimaryContactEmail.test(value);
        break;
      case "password":
        // Password cannot be empty and cannot start or end with a space
        valid = value.length > 0 && value === value.trim();
        break;
      case "confirmPassword":
        valid = value === formData.find(it => it.id === "password")?.value;
        break;
      case "birthday":
        valid = dateError;
        break;
      case "ohip":
        // OHIP is now required, so empty values are invalid
        if (!value || value.trim() === "") {
          valid = false;
        } else {
          // If provided, validate the format (4 digits, 3 digits, 3 digits)
          const cleanValue = value.replace(/\s/g, ""); // Remove spaces
          if (cleanValue.length === 10 && /^\d{10}$/.test(cleanValue)) {
            valid = true;
          } else {
            valid = false;
          }
        }
        break;
      case "details":
        valid = !!value.trim();
        break;
      default:
        break;
    }
    return valid;
  };

  const validateForm = () => {
    return !(formData.map(v => v.isValid).includes(false) || dateError);
  };

  const handleInputChange = (e: any | string) => {
    let value = "";
    value = e.target.value;
    let cursorPosition = e.target.selectionEnd;
    switch (e.currentTarget.name) {
      case "phone":
        value = formatPhoneNumber(e.currentTarget.value);
        if (cursorPosition === 4 || cursorPosition === 8) {
          cursorPosition++;
        }
        break;
      case "birthday":
        const dateVals = value.split("-");
        value = e.currentTarget.value;
        if (parseInt(dateVals[0]) > 9999) {
          dateVals[0] = dateVals[0].substring(0, value.length - 1);
          value = dateVals.join("-");
        }
        break;
      case "confirmPassword":
        const passwordData = formData.filter(e => e.id === "password");
        const isValid = passwordData[0].value === value;
        const validationErrors = [...pwdValidations];
        validationErrors[5] = { ...validationErrors[5], isValid };
        setPwdValidations(validationErrors);
        break;
      case "password":
        const newValidations = pwdValidations.map(item => {
          const isValid = validatePassword(item.id, value);

          return { ...item, isValid };
        });
        setPwdValidations(newValidations);
        break;
      default:
        break;
    }

    const valid = validateField(e.currentTarget.name, value);

    //getting state
    const newFromState = formData.map(item => {
      if (item.id === e.currentTarget.name) {
        return { ...item, value, isValid: valid, cursorPosition };
      } else {
        return { ...item, cursorPosition };
      }
    });
    setFormData(newFromState);
  };

  const handleBlur = (id: string, value: string) => {
    const index = formData.findIndex(item => item.id === id);
    if (index !== -1) {
      const newFormData = [
        ...formData.slice(0, index),
        {
          ...formData[index],
          value,
        },
        ...formData.slice(index + 1),
      ];
      dispatch(updateRequestFormData(newFormData));
    }
  };

  const changeDate = (dateVal: string) => {
    setFormData([...formData.slice(0, 3), { ...formData[3], value: dateVal?.toString(), isValid: dateVal !== "" }, ...formData.slice(4)]);

    // 18+ age validation on change
    if (dateVal) {
      if (isValidDate(dateVal)) {
        const age = calculateAge(dateVal);
        if (age < 18) {
          setDateError(true);
          setDobErrorMsg("You must be at least 18 years old");
        } else {
          setDateError(false);
          setDobErrorMsg("Valid date required");
        }
      }
    }
  };

  const changeOhipValue = (val: string) => {
    const ohipValid = validateField("ohip", val);
    setFormData([...formData.slice(0, 5), { ...formData[5], value: val, isValid: ohipValid }, ...formData.slice(6)]);
    setOhipState(val);
  };

  const onClickSave = async () => {
    setShowMethod(false);
    setErrorInitiated(true);
    if (validateForm() && tcAccepted) {
      setSendingEmailError("");
      verifyPhoneNumber();
    }
  };

  const verifyPhoneNumber = () => {
    dispatch(sendPhoneVerificationCode({ phone: formData.filter(v => v.id === "phone")[0].value })).then(action => {
      if (action.payload) {
        setCurrentStep(PatientInviteSteps.VerfifyPhone);
      } else {
      }
    });
  };

  const finalSubmit = async () => {
    if (officeId) {
      const res = await dispatch(
        acceptPatientInvitation({
          firstName: formData[0].value,
          lastName: formData[1].value,
          email: formData[2].value,
          phone: formData[4].value,
          dateOfBirth: formData[3].value,
          acceptedTerms: tcAccepted,
          ohipNumber: formData[5].value?.trim() !== "" ? formData[5].value?.trim() : undefined,
          password: formData[6].value,
          officeId: officeId,
          token: token,
        })
      );

      if (res.payload) {
        const Login = await auth?.signin({
          email: formData[2].value,
          password: formData[6].value,
          isPatient: true,
        });
        if (Login) {
          navigate("/");
        }
      }
    }
  };

  useEffect(() => {
    const newFormData = [...requestFormInitData];
    if (invitationTokenDetails) {
      const newData = newFormData.map(item => {
        const newItem = { ...item };
        switch (newItem.id) {
          case "ohip":
            newItem.value = invitationTokenDetails["ohipNumber"]?.toString() ?? "";
            const ohipValid = validateField("ohip", newItem.value);
            newItem.isValid = ohipValid;
            setOhipState(newItem.value);
            break;
          case "birthday":
            newItem.value = invitationTokenDetails["dateOfBirth"]?.toString() ?? "";
            newItem.isValid = true;
            break;
          default:
            newItem.value = invitationTokenDetails[item.id as keyof User]?.toString() ?? "";
            const valid = validateField(newItem.id, newItem.value);
            newItem.isValid = valid;
            break;
        }
        return newItem;
      });
      setFormData(newData);
    } else {
      setFormData(newFormData);
    }
  }, [invitationTokenDetails]);
  const phoneNumber = formData.find(item => item.id === "phone")?.value ?? "";

  const handleClick = async () => {
    setErrorInitiated(true);
    // Enforce 18+ validation on submit
    const birthdayValue = formData.find(v => v.id === "birthday")?.value as string;
    if (birthdayValue) {
      if (isValidDate(birthdayValue)) {
        const age = calculateAge(birthdayValue);
        if (age < 18) {
          setDobErrorMsg("You must be at least 18 years old");
          setDateError(true);
          setErrorInitiated(true);
          return;
        }
      }
    }
    if (validateForm() && tcAccepted) {
      const newEmail = formData.filter(v => v.id === "email")[0].value;
      if (invitationTokenDetails?.email !== newEmail) {
        const res = await dispatch(emailExists({ email: newEmail }));
        if (res?.payload) {
          setSendingEmailError("An account already exists with this email address.");
          return;
        }
      }
      setShowMethod(true);
      setSendingEmailError("");
    }
  };

  const sendOTPToEmail = async (resend?: boolean) => {
    setShowMethod(false);
    const res = await dispatch(
      sendEmailVerificationCode({
        email: formData.filter(v => v.id === "email")[0].value,
        firstName: formData.filter(v => v.id === "firstName")[0].value,
        lastName: formData.filter(v => v.id === "lastName")[0].value,
      })
    );
    if (res) {
      setCurrentStep(PatientInviteSteps.VerifyEmail);
    }
  };

  return (
    <>
      <HomePageHeader />
      <main>
        <div className="w-full">
          <div className="w-full max-w-2xl mx-auto">
            {currentStep === PatientInviteSteps.Register ? (
              <>
                <h3 className="text-xl text-center font-medium mb-5 text-docsigna-blue-light">
                  Complete the below required fields in order to set up your account.
                </h3>
                {sendingEmailError ? <p style={{ color: colors.Red }}>{sendingEmailError}</p> : null}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.map(item => {
                    if (item.id === "birthday") {
                      return (
                        <div className="py-0.5 flex" key={item.id}>
                          <div className="w-full">
                            <DatePicker
                              label={item.label}
                              isRequired={item.required}
                              name={item.id}
                              placeholder={item.placeholder}
                              value={item.value}
                              isError={dateError && errorInitiated}
                              onChange={val => changeDate(val)}
                              errorMsg={dobErrorMsg}
                              onError={(e: boolean) => setDateError(e)}
                              showError={errorInitiated}
                            />
                          </div>
                        </div>
                      );
                    } else if (item.id === "password" || item.id === "confirmPassword") {
                      return (
                        <div className="py-0.5" key={item.id}>
                          <TextInput
                            isError={!item.isValid && errorInitiated}
                            isRequired={item.required}
                            isPassword={passwordFlds.includes(item.id)}
                            name={item.id}
                            label={item.label}
                            value={String(item.value)}
                            placeholder={item.placeholder}
                            errorMsg={
                              item.id === "password" && item.value.length > 0 && item.value !== item.value.trim()
                                ? "Password cannot start or end with a space."
                                : item.errorMesage
                            }
                            onChangeFunc={e => handleInputChange(e)}
                          />
                        </div>
                      );
                    } else if (item.id === "ohip") {
                      return (
                        <div className="py-0.5" key={item.id}>
                          <label className="text-sm text-docsigna-blue-dark font-medium block w-full">
                            {item.label}
                            {item.required ? <span className="text-red-500">*</span> : null}
                          </label>
                          {/* Hidden honeypot field to confuse browser autofill */}
                          <input type="text" name="cc-number" style={{ display: "none" }} tabIndex={-1} autoComplete="off" />
                          <NumberFormat
                            value={item.value}
                            isNumericString
                            format={"#### ### ###"}
                            placeholder="Health Card #"
                            displayType="input"
                            inputMode="numeric"
                            name={`hc_${Date.now()}`}
                            id="provincial-health-id"
                            autoComplete="one-time-code"
                            data-form-type="other"
                            data-lpignore="true"
                            data-1p-ignore="true"
                            aria-autocomplete="none"
                            className={`general-ohip ${!item.isValid && errorInitiated ? "error" : ""}`}
                            onValueChange={values => {
                              changeOhipValue(values.value);
                            }}
                          />
                          {!item.isValid && errorInitiated ? <p style={{ color: colors.Red }}>{item.errorMesage}</p> : null}
                        </div>
                      );
                    } else {
                      return (
                        <div className="py-0.5" key={item.id}>
                          <TextInput
                            isError={!item.isValid && errorInitiated}
                            isRequired={item.required}
                            isPassword={passwordFlds.includes(item.id)}
                            name={item.id}
                            label={`${item.label}`}
                            value={String(item.value)}
                            placeholder={item.placeholder}
                            errorMsg={item.errorMesage}
                            onChangeFunc={e => handleInputChange(e)}
                            onBlur={e => handleBlur(item.id, e.currentTarget.value.trim())}
                          />
                        </div>
                      );
                    }
                  })}
                  <div>
                    {formData[6].value &&
                      pwdValidations.map(item => {
                        return (
                          <div key={item.id} className="my-1 ml-2">
                            <PasswordErrorList isValid={item.isValid} message={item.msg} />
                          </div>
                        );
                      })}
                  </div>
                </div>
                <div className="py-2 mb-2">
                  <label className="flex justify-start items-center">
                    <Checkbox checked={tcAccepted} onChange={() => handleTermsCheckbox()} label="" />
                    <span className="text-sm font-medium">
                      * I agree to Docnote&apos;s{" "}
                      <span className="text-sm text-docsigna-blue-light" onClick={() => handleClickOpenTerms()}>
                        Terms and Conditions
                      </span>{" "}
                      and{" "}
                      <span className="text-sm text-docsigna-blue-light" onClick={() => handleClickOpenPrivacyPolicy()}>
                        Privacy Policy
                      </span>
                    </span>
                  </label>
                  {!tcAccepted && errorInitiated ? <p style={{ color: colors.Red }}>{"You must agree to Docnote terms to continue"}</p> : null}
                </div>
                <div className="py-2 flex justify-around">
                  <Button
                    onClick={() => {
                      navigate("/");
                    }}
                    className="pointer px-5 mr-3"
                    text={"Cancel"}
                    disabled={loading || sendingEmail || pending}
                    width="fit"
                    variant="Secondary"
                  />
                  <Button
                    onClick={() => {
                      handleClick();
                    }}
                    className="pointer px-5 mr-3"
                    text={"Complete Registration"}
                    disabled={!validateForm() && errorInitiated}
                    variant={sendingEmail || pending || loading ? "loading" : "Primary"}
                  />
                </div>
              </>
            ) : null}
            {currentStep === PatientInviteSteps.VerifyEmail ? (
              <VerifyEmail email={formData[2].value} emailVerified={finalSubmit} onBack={() => setCurrentStep(PatientInviteSteps.Register)} />
            ) : null}
            {currentStep === PatientInviteSteps.VerfifyPhone ? (
              <VerifyPhone phone={phoneNumber} phoneVerified={finalSubmit} onBack={() => setCurrentStep(PatientInviteSteps.Register)} />
            ) : null}
          </div>
        </div>
      </main>
      <ConfirmationDialog
        maxWidth="lg"
        successButtonText=""
        failureButtonText="Close"
        open={openTerms}
        description={<TermsAndConditions showCheckbox={false} />}
        handleSuccess={() => {}}
        handleFailure={handleCloseTerms}
      />
      <ConfirmationDialog
        maxWidth="lg"
        successButtonText=""
        failureButtonText="Close"
        open={openPrivacyPolicy}
        description={<PrivacyPolicy />}
        handleSuccess={() => {}}
        handleFailure={handleClosePrivacyPolicy}
      />
      <ConfirmationDialog
        open={showMethod}
        description={"Choose a method to verify your account."}
        descriptionPosition={true}
        actionButtonPosition="Center"
        successButtonText={"Mobile Phone Number"}
        failureButtonText={"Email"}
        handleSuccess={onClickSave}
        handleFailure={sendOTPToEmail}
        modalVerify={true}
        canClickOutSide={false}
      />
    </>
  );
};
export default InvitationForm;
